/* eslint-disable max-lines */
import moment from 'moment';
import _ from 'lodash';
import { Badge, LinkCopied } from './repositories-modal.components';
import { Tooltip } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import { LINWEB_URI } from 'common/constants/api-urls';
import {
  DATE_FORMAT_WITH_FULL_DAY,
  MONTH_AND_YEAR_FORMAT,
  MONTHS_PARAM,
  SESSION_STORAGE_KEYS,
  TRACKING
} from 'common/constants/constants';
import * as ZendeskService from 'common/services/zendesk.service';
import { getColor } from 'common/constants/_colors';
import Text from 'components/base/Text';
import { Flex } from 'components/base/layout-components';
import * as TrackingService from 'common/services/tracking.service';
import {
  fetchGitUserDetails,
  getFetchRepositoriesV2
} from 'common/services/repositories.service';
import Checkbox from 'components/base/Checkbox';
import { SPACES } from 'common/constants/sizes';
import Icon from 'components/base/Icon';
import { MAX_REPO_NUM_FOR_FREE_DORA_DURING_ONBOARDING } from './repositories.modal.consts';

const INACTIVE_REPO_THRESHOLD_IN_MONTHS = 3;
export const NO_GIT_ORGS_ERROR = 'no_git_orgs';

export const isInactiveRepository = updatedAt =>
  updatedAt &&
  moment().diff(moment(updatedAt), MONTHS_PARAM) >=
    INACTIVE_REPO_THRESHOLD_IN_MONTHS;

export const getActiveRepositories = repositories =>
  repositories
    ? repositories.filter(
        ({ isInactive, isDiscovered }) => !isInactive && !isDiscovered
      )
    : [];

export const findRelevantSensorAuthorization = (
  gitIntegrations,
  sensor_authorization_id
) =>
  sensor_authorization_id
    ? gitIntegrations.find(({ id }) => sensor_authorization_id === id)
    : _.get(gitIntegrations, '[0]');

export const shouldShowNoReposTemplate = activeRepos => !activeRepos.length;

export const inviteYourAdmin = (toggleSnackBar, { id: accountId }) => {
  copy(`${LINWEB_URI}?${SESSION_STORAGE_KEYS.REF_ACCOUNT_ID}=${accountId}`);

  toggleSnackBar({
    message: <LinkCopied />,
    variant: 'success'
  });
};

export const talkToUs = () => {
  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
    page: TRACKING.PAGES.ONBOARDING,
    page_tab: TRACKING.PAGE_TABS.SELECT_REPOS,
    click_type: TRACKING.CLICK.TALK_TO_US
  });
  ZendeskService.openSupportRequest();
};

const createdColumn = ({ isNew, created_at, fontColor, iconColor, number }) => {
  const createdText = (
    <Text
      size='small'
      color={fontColor}
      pl={SPACES['x-small']}
      whiteSpace='nowrap'
    >
      {isNew
        ? `${number ? number + ' ' : ''}NEW! added ${moment(created_at).format(
            DATE_FORMAT_WITH_FULL_DAY
          )}`
        : moment(created_at).format(MONTH_AND_YEAR_FORMAT)}
    </Text>
  );

  return isNew ? (
    <>
      <Icon name='fibermanualrecordicon' color={iconColor} size='small-shape' />
      {createdText}
    </>
  ) : (
    createdText
  );
};

export const getRepoModalTableColumns = ({ numOfActiveRepositories }) => {
  return [
    {
      ColumnHeaderComponent: () => (
        <Flex
          height='100%'
          width='100%'
          justify='center'
          items='center'
          content='center'
        />
      )
    },
    {
      name: 'REPOSITORIES',
      dataPoint: 'name'
    },
    {
      name: 'PATH',
      dataPoint: 'path'
    },
    {
      name: 'ACTIVE',
      dataPoint: 'active',
      ColumnHeaderComponent: () => (
        <Text textTransform='uppercase' color='gray' whiteSpace='nowrap'>
          {numOfActiveRepositories} ACTIVE REPOS
        </Text>
      )
    },
    {
      name: 'CREATED',
      dataPoint: 'created'
    }
  ];
};

export const getRepoModalTableData = ({
  providerConfig = {},
  repositories,
  isRepoDisabled,
  isRepoSelected,
  toggleRepositorySelection,
  numOfFreeSlots
}) =>
  repositories.map(
    ({
      id,
      name,
      http_url,
      created_at = null,
      isNew,
      isInactive,
      isDiscovered
    }) => {
      const inactiveFontColor = 'silver';
      const fontColor =
        isInactive || isDiscovered ? inactiveFontColor : 'osloGray';
      const iconColor =
        isInactive || isDiscovered ? inactiveFontColor : 'dodgerBlue';
      const backgroundColor = isDiscovered ? 'fetaLight' : 'catskillGray';

      return {
        select: (
          <Tooltip
            interactive
            placement='top-start'
            disableHoverListener={
              isRepoSelected(id) ||
              (numOfFreeSlots > 0 && !isInactive && !isDiscovered)
            }
            title={
              isDiscovered ? (
                <Text size='small' pb='0.3rem'>
                  This repository was alreay discovered.
                </Text>
              ) : isInactive ? (
                <>
                  <Text size='small' pb='0.3rem'>
                    This repository has not been active in the last 3 months.
                  </Text>
                  <Text size='small'>Scanning it will show no results</Text>
                </>
              ) : numOfFreeSlots <= 0 ? (
                <Text>You have reached your limit of repositories</Text>
              ) : (
                <Text>
                  Select team repos to proceed. You will be able to add personal
                  repos after onboarding
                </Text>
              )
            }
          >
            <Flex justify='center' items='center' content='center'>
              <Checkbox
                style={{
                  opacity: isDiscovered ? '0.5' : 1
                }}
                data-cy='repository'
                type='checkbox'
                disabled={isRepoDisabled(id) || isInactive || isDiscovered}
                onClick={() => {
                  toggleRepositorySelection(id);
                }}
                checked={(isRepoSelected(id) && !isInactive) || isDiscovered}
              />
            </Flex>
          </Tooltip>
        ),
        name: (
          <Flex items='center'>
            <Text
              size='medium'
              color={isInactive || isDiscovered ? inactiveFontColor : 'shark'}
              ellipsisAfter='14rem'
            >
              {name}
            </Text>
          </Flex>
        ),
        path: (
          <Flex items='center'>
            <Text size='small' color={fontColor}>
              {_.get(providerConfig.formatHttpUrl(http_url), 'displayPath', '')}
            </Text>
          </Flex>
        ),
        active: (
          <Flex items='center'>
            {isDiscovered ? (
              <Badge type='inactive'>DISCOVERED</Badge>
            ) : isInactive ? (
              <Badge type='inactive'>NOT ACTIVE</Badge>
            ) : (
              <Badge type='active'>
                <div
                  style={{
                    display: 'inline-block',
                    marginRight: '0.5rem',
                    width: '0.6rem',
                    height: '0.6rem',
                    borderRadius: '50%',
                    backgroundColor: getColor('pastelGreen')
                  }}
                />
                ACTIVE
              </Badge>
            )}
          </Flex>
        ),
        ...(isInactive || isDiscovered
          ? {
              styles: {
                disabled: true,
                color: getColor('silverSand'),
                backgroundColor: getColor(backgroundColor)
              }
            }
          : {}),
        created: (
          <Flex items='center'>
            {created_at
              ? createdColumn({ isNew, created_at, fontColor, iconColor })
              : ''}
          </Flex>
        )
      };
    }
  );

export const getRepositoriesFromProvider = async ({
  sensor_authorization_id,
  repositories,
  isOnboarding,
  providerConfig,
  selectedOrg,
  rowsPerPage,
  paramCursor,
  q,
  githubFilterByRepoType,
  isBitbucket
}) => {
  try {
    const providerRepositories = await getFetchRepositoriesV2({
      sensor_authorization_id,
      providerConfig,
      git_org: selectedOrg,
      page_size: rowsPerPage,
      cursor: paramCursor,
      q,
      githubFilterByRepoType
    });

    const { total_repos = null, cursor = null } = providerRepositories;
    let { repo_data = [] } = providerRepositories;
    // convert repos id to string, to have common type for providers that the id is guid or number
    repo_data = repo_data.map(repo => ({ ...repo, id: repo.id.toString() }));

    const activeRepos = getActiveRepositories(repositories);
    TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
      page: isOnboarding ? TRACKING.PAGES.ONBOARDING : TRACKING.PAGES.SETTINGS,
      page_tab: TRACKING.PAGE_TABS.SELECT_REPOS,
      context: {
        nTotalRepos: repositories.length,
        nActiveRepos: activeRepos.length
      }
    });

    return {
      total: isBitbucket ? -1 : total_repos,
      cursor,
      items: repo_data || []
    };
  } catch (e) {
    return {
      error: _.get(e, 'response.status', 500),
      items: []
    };
  }
};

export const findAndDeleteSensor = ({
  isOnboarding,
  gitIntegrations,
  sensor_authorization_id,
  deleteSensorAuthorization
}) => {
  if (isOnboarding) {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.ONBOARDING,
      page_tab: TRACKING.PAGE_TABS.SELECT_REPOS,
      click_type: TRACKING.CLICK.CHANGE_AUTHORIZATION
    });
  }
  const sensor = findRelevantSensorAuthorization(
    gitIntegrations,
    sensor_authorization_id
  );
  if (sensor) {
    deleteSensorAuthorization(sensor.id);
  }
};

export const fetchOrgs = async (
  sensorAuthorizationId,
  orderByField = null,
  orderAsc = true
) => {
  const { orgs } = await fetchGitUserDetails(sensorAuthorizationId);
  return orderByField
    ? _.orderBy(orgs, [orderByField], [orderAsc ? 'asc' : 'desc'])
    : orgs;
};

export const getCursor = (pageParams, page, decodeCursor) => {
  if (page - 1 < 0) {
    return null;
  } else {
    const cursor = _.get(pageParams, `[${page - 1}].next`, null);
    return decodeCursor ? decodeURIComponent(cursor) : cursor;
  }
};

export const clearPagingParams = ({ setPage, setPageParams }) => {
  setPageParams({});
  setPage(0);
};

export const isValidTerm = (term, searchCharsLimit) =>
  term === '' || term.length >= searchCharsLimit;

export const destructureSelected = selectedByOrg => {
  const selectAllObj = _.get(selectedByOrg, 'selectAll', {});
  const isSelectAll = _.get(selectAllObj, 'isSelectAll', false);
  const excluded = _.get(selectAllObj, 'excluded', new Set());
  const selectSpecificObj = _.get(selectedByOrg, 'specific', {});
  const specificIds = Object.keys(selectSpecificObj);
  const totalReposByOrg = _.get(selectedByOrg, 'total', 0);
  const inactiveRepos = _.get(selectedByOrg, 'inactiveRepos', new Set());
  const isFoundActiveRepos = _.get(selectedByOrg, 'isFoundActiveRepos', false);

  return {
    isSelectAll,
    selectAllObj,
    excluded,
    selectSpecificObj,
    specificIds,
    totalReposByOrg,
    inactiveRepos,
    isFoundActiveRepos
  };
};

const getTotalSelectedRepositoriesByOrg = ({ selectedByOrg }) => {
  const { isSelectAll, excluded, specificIds, totalReposByOrg, inactiveRepos } =
    destructureSelected(selectedByOrg);

  return isSelectAll
    ? totalReposByOrg - inactiveRepos.size - excluded.size
    : specificIds.length;
};

export const getTotalSelectedRepositories = selected =>
  Object.values(selected).reduce(
    (acc, value) =>
      acc +
      getTotalSelectedRepositoriesByOrg({
        selectedByOrg: value
      }),
    0
  );

export const getSelectedReposMessage = ({ selected }) =>
  Object.entries(selected).map(([key, value]) => {
    const { isSelectAll, excluded, totalReposByOrg } =
      destructureSelected(value);

    const numOfSelectedReposByOrg = getTotalSelectedRepositoriesByOrg({
      selectedByOrg: value
    });
    if (numOfSelectedReposByOrg === 0) return null;

    if (isSelectAll) {
      return (
        <Flex mb='x-small' items='center' key={key}>
          <Text size='medium' weight='bold'>
            {_.capitalize(key)}: All discovered repositories selected (
            {excluded.size} excluded).
          </Text>
        </Flex>
      );
    } else {
      return (
        <Flex mb='x-small' items='center' key={key}>
          <Text size='medium' weight='bold'>
            {_.capitalize(key)}: {numOfSelectedReposByOrg}
            {totalReposByOrg === -1 ? '' : `/${totalReposByOrg}`} discovered
            repositories selected.
          </Text>
        </Flex>
      );
    }
  });

export const getFreeSlotsMessage = ({
  numOfFreeSlots,
  isTeamLicense,
  maxRepos,
  isOnboarding,
  selected
}) => {
  const orgName = Object.keys(selected)?.[0];
  const { total } = orgName ? selected?.[orgName] : -1;
  if (
    total > MAX_REPO_NUM_FOR_FREE_DORA_DURING_ONBOARDING &&
    isOnboarding &&
    isTeamLicense
  ) {
    return (
      <Text size='medium'>
        {`${
          maxRepos - numOfFreeSlots
        }/5 repositories will be added. You can add more repositories after finishing the onboarding.`}
      </Text>
    );
  }

  if (numOfFreeSlots < 0) {
    return (
      <Text size='medium'>
        {`${
          isTeamLicense ? 'Our free' : 'Your'
        } plan is limited to ${maxRepos} repos`}
      </Text>
    );
  }

  return (
    <Text size='medium'>
      {`${numOfFreeSlots} available slots in ${
        isTeamLicense ? 'our free' : 'your'
      } ${maxRepos} repos plan`}
    </Text>
  );
};

export const formatSelectedReposForSave = ({ selected }) => {
  const filters = [];
  Object.entries(selected).forEach(([key, value]) => {
    const org = key;
    const { isSelectAll, excluded, selectSpecificObj } =
      destructureSelected(value);

    if (isSelectAll) {
      filters.push({
        mode: 'select_all',
        org,
        excluded: Array.from(excluded).map(String)
      });
    } else {
      const specificFilters = {};
      Object.entries(selectSpecificObj).forEach(([id, value]) => {
        const key = `${value.q || ''}_${value.cursor || ''}`;
        if (!specificFilters[key]) {
          specificFilters[key] = {
            filter: value.q || '',
            cursor: value.cursor || '',
            ids: []
          };
        }
        specificFilters[key]['ids'].push(id);
      });

      Object.values(specificFilters).forEach(filterData => {
        filters.push({
          mode: 'specific',
          org,
          filter: filterData['filter'] || '',
          cursor: filterData['cursor'] || '',
          included: filterData['ids'] || []
        });
      });
    }
  });

  return filters;
};

export const handleSelection = ({
  repositoriesIds = [],
  selectedByOrg = {},
  action = 'selectAllInPage',
  term = '',
  cursor = '',
  unselectedForNewRepos,
  setUnselectedForNewRepos
}) => {
  const {
    isSelectAll,
    selectAllObj,
    excluded,
    selectSpecificObj,
    specificIds
  } = destructureSelected(selectedByOrg);

  if (isSelectAll) {
    if (
      action === 'selectAllInPage' ||
      (action === 'toggleRepo' && excluded.has(repositoriesIds[0]))
    ) {
      repositoriesIds.forEach(id => excluded.delete(id));
    } else {
      repositoriesIds.forEach(id => excluded.add(id));
    }

    return {
      selectAll: {
        ...selectAllObj,
        excluded
      }
    };
  } else {
    if (
      action === 'selectAllInPage' ||
      (action === 'toggleRepo' && !specificIds.includes(repositoriesIds[0]))
    ) {
      repositoriesIds.forEach(id => {
        Object.assign(selectSpecificObj, {
          [id]: {
            q: term,
            cursor
          }
        });
      });

      return { specific: selectSpecificObj };
    } else {
      repositoriesIds.forEach(id => {
        delete selectSpecificObj[id];
        unselectedForNewRepos.add(id);
      });
      setUnselectedForNewRepos(new Set(unselectedForNewRepos));

      return { specific: selectSpecificObj };
    }
  }
};

export const getOrgValueFromNewRepos = ({ newRepositories = [], orgs }) => {
  // set dropdown with repo org from new Repositories (from message center)
  try {
    const newGitOrg = new URL(newRepositories[0].http_url).pathname.split(
      '/'
    )[1];

    return newGitOrg
      ? _.get(
          _.find(orgs, ({ value }) => value === newGitOrg),
          'value',
          orgs[0].value
        )
      : orgs[0].value;
  } catch (e) {
    return orgs[0].value;
  }
};

export const hasInvalidCMRepo = (selected, orgRepositories) => {
  if (!orgRepositories || !Array.isArray(orgRepositories)) {
    return false;
  }

  return Object.values(selected).some(selectedByOrg => {
    const { isSelectAll, excluded, selectSpecificObj } =
      destructureSelected(selectedByOrg);

    if (isSelectAll) {
      // Check all repositories except excluded ones
      return orgRepositories.some(repo => {
        const isExcluded = excluded.has(repo.id);
        const isInvalid =
          repo.name.toLowerCase() === 'cm' && repo.name !== 'cm';
        return !isExcluded && isInvalid;
      });
    } else {
      // Check only specifically selected repositories
      const selectedIds = Object.keys(selectSpecificObj);
      return orgRepositories.some(repo => {
        const isSelected = selectedIds.includes(repo.id);
        const isInvalid =
          repo.name.toLowerCase() === 'cm' && repo.name !== 'cm';
        return isSelected && isInvalid;
      });
    }
  });
};
